//#region import libraries
import React, { useState, useEffect } from 'react';

// custom components
import { CustomSpinner } from "_atoms";
import { AssistanceSelector } from "_molecules";

// bootstrap
import { Modal, Row, Col, Form, Button } from 'react-bootstrap';

// services
import * as BookingService from "_services/booking-service";

// styles
import { Colors } from '_styles';
//#endregion

// passenger app v2 stores the airport arrival time in paxAppMetadata; older app and portal bookings use dueTime
export const getDueTime = (doc) => {
    const raw = String(doc?.dueTime || doc?.paxAppMetadata?.airportArrivalTime || "").trim();
    const match = raw.match(/^(\d{1,2}):(\d{2})/);
    return match ? `${match[1].padStart(2, "0")}:${match[2]}` : "";
};

const formatFlightTime = (value) => {
    if (!value || isNaN(new Date(value).getTime())) return "N/A";
    // flight times carry the airport's local time, so read it from the string rather than the browser timezone
    const match = String(value).match(/T(\d{2}:\d{2})/);
    return match ? match[1] : "N/A";
};

const EditRequestModal = ({ show, booking, onHide, onSaved }) => {

    //#region local states
    const [assistance, setAssistance] = useState([]);
    const [referenceNumber, setReferenceNumber] = useState("");
    const [dueTime, setDueTime] = useState("");
    const [bio, setBio] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    //#endregion

    // autofill from the selected booking each time the modal opens
    useEffect(() => {
        if (!show || !booking) return;
        setAssistance(Array.isArray(booking.assistance) ? booking.assistance : []);
        setReferenceNumber(booking.referenceNumber && booking.referenceNumber !== "N/A" ? booking.referenceNumber : "");
        setDueTime(getDueTime(booking));
        setBio(typeof booking.bio === "string" ? booking.bio.trim() : "");
        setError("");
    }, [show, booking]);

    const save = async () => {
        if (!booking?._id) return;
        if (assistance.length === 0) {
            setError("Select at least one assistance code.");
            return;
        }

        const body = {
            assistance,
            referenceNumber: referenceNumber.trim().toUpperCase(),
            dueTime,
            bio,
        };
        // keep the passenger app copy in step so the app shows the same time
        if (booking.paxAppMetadata) {
            body.paxAppMetadata = { ...booking.paxAppMetadata, airportArrivalTime: dueTime };
        }

        setSaving(true);
        setError("");
        try {
            await BookingService.updateBooking({ id: booking._id, body });
            onSaved();
        } catch (e) {
            setError("Could not update this booking, please try again.");
        } finally {
            setSaving(false);
        }
    };

    const flight = booking?.flightConfirmationDetails || {};
    const flightNumber = flight.carrierCode && flight.flightNumber
        ? flight.carrierCode + flight.flightNumber
        : (booking?.flightDetails?.carrierCode || "") + (booking?.flightDetails?.flightNumber || "");

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Passenger Information</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {booking && (
                    <Form>
                        {/* read-only booking summary */}
                        <div style={styles.summary}>
                            <Row>
                                <Col xs={12} md={6}>
                                    <p style={styles.summaryLabel}>Passenger</p>
                                    <p style={styles.summaryValue}>{booking.name || "N/A"}</p>
                                </Col>
                                <Col xs={6} md={3}>
                                    <p style={styles.summaryLabel}>Flight</p>
                                    <p style={styles.summaryValue}>{flightNumber || "N/A"}</p>
                                </Col>
                                <Col xs={6} md={3}>
                                    <p style={styles.summaryLabel}>Date</p>
                                    <p style={styles.summaryValue}>{booking.flightDetails?.departureDate || flight.departureDate || "N/A"}</p>
                                </Col>
                            </Row>
                            <Row className="mt-2">
                                <Col xs={6} md={3}>
                                    <p style={styles.summaryLabel}>Route</p>
                                    <p style={styles.summaryValue}>{flight.origin && flight.destination ? `${flight.origin} → ${flight.destination}` : "N/A"}</p>
                                </Col>
                                <Col xs={6} md={3}>
                                    <p style={styles.summaryLabel}>Departure</p>
                                    <p style={styles.summaryValue}>{formatFlightTime(flight.departureTime)}</p>
                                </Col>
                                <Col xs={6} md={3}>
                                    <p style={styles.summaryLabel}>Arrival</p>
                                    <p style={styles.summaryValue}>{formatFlightTime(flight.arrivalTime)}</p>
                                </Col>
                                <Col xs={6} md={3}>
                                    <p style={styles.summaryLabel}>Owner</p>
                                    <p style={{ ...styles.summaryValue, ...styles.mono }} title={booking.userId || ""}>{booking.portal && booking.email?.includes("@") ? booking.email : (booking.userId || "N/A")}</p>
                                </Col>
                            </Row>
                        </div>

                        {/* editable passenger details */}
                        <Form.Group className="mt-3">
                            <Form.Label style={styles.label}>Assistance Requirements</Form.Label>
                            <AssistanceSelector setAssistance={setAssistance} value={assistance} isBooking={true} />
                        </Form.Group>

                        <Row className="mt-3">
                            <Col xs={12} md={6}>
                                <Form.Label style={styles.label}>Airline Booking Reference <span style={styles.optional}>(Optional)</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    style={{ textTransform: 'uppercase' }}
                                    value={referenceNumber}
                                    onChange={(e) => setReferenceNumber(e.target.value)}
                                />
                            </Col>
                            <Col xs={12} md={6} className="mt-3 mt-md-0">
                                <Form.Label style={styles.label}>Due Time <span style={styles.optional}>(local time at the airport)</span></Form.Label>
                                <Form.Control
                                    type="time"
                                    value={dueTime}
                                    onChange={(e) => setDueTime(e.target.value)}
                                />
                            </Col>
                        </Row>

                        <Form.Group className="mt-3">
                            <Form.Label style={styles.label}>Notes <span style={styles.optional}>(Optional)</span></Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder="Any additional notes on the booking"
                                value={bio}
                                onChange={(e) => setBio(e.target.value)}
                            />
                        </Form.Group>

                        {error && <p style={styles.error}>{error}</p>}
                    </Form>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onHide} disabled={saving}>Cancel</Button>
                <Button variant="outline-primary" onClick={save} disabled={saving} style={{ width: 150 }}>
                    {saving ? <CustomSpinner /> : "Save Changes"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const styles = {
    summary: {
        background: '#FAFBFC',
        border: '1px solid #E8ECF0',
        borderRadius: 10,
        padding: '12px 16px',
    },
    summaryLabel: {
        fontSize: 12,
        color: Colors.GRAY_DARK,
        marginBottom: 2,
    },
    summaryValue: {
        fontSize: 14,
        fontWeight: 500,
        color: Colors.TEXT_COLOR,
        marginBottom: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    mono: {
        fontFamily: 'monospace',
        fontSize: 12,
    },
    label: {
        fontSize: 13,
        fontWeight: 500,
        color: Colors.TEXT_COLOR,
        marginBottom: 4,
    },
    optional: {
        fontWeight: 400,
        color: Colors.GRAY_DARK,
    },
    error: {
        color: Colors.DARK_RED,
        fontSize: 13,
        marginTop: 12,
        marginBottom: 0,
    },
};

export default EditRequestModal;
